import { useEffect, useState } from 'react';
import { useLocation } from '@direct-frontend/modules-router';
import { noop } from '@direct-frontend/stdlib';
import { type OpenApiSchemaCommunication } from '#shared/api/schema';
import { getSafeUserRequestContext } from '#shared/lib/context';
import { useLazyGetCommunications, useMarkCommunicationAsViewed } from '../api';
import { selectBannerCommunication } from './selectBannerCommunication';
import { selectModalCommunication } from './selectModalCommunication';

export const useCommunications = () => {
    const { pathname, search } = useLocation();
    const { authed } = getSafeUserRequestContext();
    const [fetchCommunications, { currentData, isError }] = useLazyGetCommunications();
    const [sendViewed] = useMarkCommunicationAsViewed();
    const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());
    const [hasLoaded, setHasLoaded] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);

    useEffect(() => {
        if (!authed) {
            return;
        }

        let active = true;

        void fetchCommunications()
            .then((result) => {
                if (active) {
                    setHasLoaded(true);
                    setHasFailed(result.isError);
                }
            })
            .catch(() => {
                if (active) {
                    setHasLoaded(true);
                    setHasFailed(true);
                }
            });

        return () => {
            active = false;
        };
    }, [authed, fetchCommunications]);

    const communications = hasLoaded && !hasFailed && !isError ? (currentData?.communications ?? []) : [];
    const searchParams = communications.length > 0 ? new URLSearchParams(search) : null;
    const banner = searchParams
        ? selectBannerCommunication(communications, pathname, searchParams, dismissedIds)
        : undefined;
    const modal = searchParams
        ? selectModalCommunication(communications, pathname, searchParams, dismissedIds)
        : undefined;

    const markViewed = (id: string): void => {
        sendViewed({ id }).catch(noop);
    };

    const close = (id: string) => {
        setDismissedIds((previous) => new Set(previous).add(id));
        markViewed(id);
    };

    // Возвращает true, если действие закрывает коммуникацию, и false, если она остаётся открытой.
    const action = (communication: OpenApiSchemaCommunication) => {
        if (communication.button?.url) {
            markViewed(communication.id);

            return false;
        }

        // Если кнопка без ссылки, то поведение как у кнопки закрыть (для реализации кнопки "Понятно, спасибо").
        close(communication.id);

        return true;
    };

    return { banner, modal, isPending: authed && !hasLoaded, close, action };
};
